import React, { useState } from "react";
import { api } from "../../lib/api";
import ClientTabs from "./ClientTabs";
import { useActiveClients } from "../../hooks/useActiveClients";
import { ManagerView } from "../ManagerPortal";
import { WorkerView } from "../WorkerPortal";
import { ReviewView } from "../ReviewPortal";
import { Eye, Users, HardHat, ClipboardCheck } from "lucide-react";

const ROLE_TABS = [
  { id: "manager", label: "Manager Portal", icon: Users },
  { id: "worker", label: "Team Portal", icon: HardHat },
  { id: "review", label: "Review Portal", icon: ClipboardCheck },
];

function ReadOnlyBanner() {
  return (
    <div data-testid="client-view-banner" className="flex items-start gap-3 rounded-xl border px-4 py-3 mb-4"
      style={{ backgroundColor: "#FEF0E6", borderColor: "#FDBA74" }}>
      <Eye size={18} className="flex-shrink-0 mt-0.5" style={{ color: "#F97316" }} />
      <p className="text-sm" style={{ color: "#9A3412" }}>
        <span className="font-semibold">Read-only view.</span> This renders the client-facing portal using your
        admin session — no client session exists, so nothing you do here can ever be recorded as a client action.
        All approve, submit, tick, comment and feedback controls are absent.
      </p>
    </div>
  );
}

function RoleTabs({ role, setRole }) {
  return (
    <div className="flex gap-1 mb-4 p-1 rounded-xl border w-fit" style={{ borderColor: "#E5E7EB" }} data-testid="client-view-role-tabs">
      {ROLE_TABS.map((r) => {
        const Icon = r.icon;
        const isActive = role === r.id;
        return (
          <button key={r.id} data-testid={`client-view-role-${r.id}`} onClick={() => setRole(r.id)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            style={isActive ? { backgroundColor: "#0A1F44", color: "#fff" } : { color: "#6B7280" }}>
            <Icon size={15} /> {r.label}
          </button>
        );
      })}
    </div>
  );
}

export default function AdminClientView() {
  const { clients, active, setActive, status: clientsStatus, error: clientsError, retry: retryClients } = useActiveClients();
  const [role, setRole] = useState("manager");

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold mb-1">Client View</h1>
      <p className="text-[#6B7280] text-sm mb-6">
        See exactly what a client sees, without logging in as them.
      </p>

      <ClientTabs clients={clients} active={active} onChange={setActive} status={clientsStatus} error={clientsError} onRetry={retryClients} />

      {active && (
        <>
          <ReadOnlyBanner />
          <RoleTabs role={role} setRole={setRole} />
          <div data-testid={`client-view-render-${role}`} className="rounded-2xl p-6 md:p-8" style={{ backgroundColor: "#0A1F44" }}>
            {role === "manager" && <ManagerView clientId={active} readOnly />}
            {role === "worker" && <WorkerView clientId={active} readOnly />}
            {role === "review" && <ReviewView clientId={active} readOnly />}
          </div>
        </>
      )}
    </div>
  );
}
